import { useState, useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AdminTable } from './AdminTable';
import AdminPageHeader from './AdminPageHeader';

interface AdminUsersProps {
  allProfiles: any[];
}

const AdminUsers = ({ allProfiles }: AdminUsersProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProfiles = useMemo(() => {
    if (!searchQuery.trim()) return allProfiles;
    const query = searchQuery.toLowerCase();
    return allProfiles.filter((p) => 
      (p.email || '').toLowerCase().includes(query) ||
      (p.display_name || '').toLowerCase().includes(query) ||
      (p.business_name || '').toLowerCase().includes(query)
    );
  }, [allProfiles, searchQuery]);

  const columns = [
    {
      header: 'Profile',
      cell: (p: any) => (
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0 ring-1 ring-primary/20">
            {p.display_name?.[0] || p.email?.[0] || 'U'}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground tracking-tight truncate">{p.display_name || 'No Name Set'}</p>
            <p className="text-xs text-muted-foreground truncate">{p.email}</p>
          </div>
        </div>
      ),
    },
    {
      header: 'Business Name',
      cell: (p: any) => (
        <div>
          <p className="text-sm font-medium text-foreground truncate max-w-[150px]">{p.business_name || '—'}</p>
          <p className="text-xs text-muted-foreground truncate max-w-[150px]">{p.website || 'No website'}</p>
        </div>
      ),
    },
    {
      header: 'Plan Status',
      cell: (p: any) => (
        <span 
          className={cn(
            "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold capitalize border",
            p.plan === 'pro' 
              ? "bg-primary/10 text-primary border-primary/20" 
              : "bg-muted text-muted-foreground border-transparent"
          )}
        >
          {p.plan || 'Free'}
        </span>
      ),
    },
    {
      header: 'Created',
      cell: (p: any) => (
        <span className="text-sm text-muted-foreground">
          {new Date(p.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      header: 'Action',
      className: 'text-right',
      cell: (p: any) => (
        <div className="flex justify-end">
          <button className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="animate-in fade-in duration-500">
      <div className="mb-6">
        <AdminPageHeader 
          title="User Management"
          description="Manage and monitor all platform users."
        />
      </div>

      <AdminTable
        data={filteredProfiles}
        columns={columns}
        keyExtractor={(item) => item.id}
        searchPlaceholder="Search users by email, name, or business..."
        onSearch={setSearchQuery}
        emptyState="No users match your search."
      />
    </div>
  );
};

export default AdminUsers;
